/**
 * Route guard that enforces authentication and an optional role check.
 *
 * - Unauthenticated → triggers Hosted UI login (once the auth config is
 *   present). In dev without Cognito config, shows a friendly notice.
 * - Authenticated but missing role → renders <AuthorizationError/>.
 * - Authenticated and authorized → renders children.
 *
 * Validates Requirement 2.5.
 */

import type { ReactNode } from "react";

import { Alert, Box, Button, SpaceBetween } from "@cloudscape-design/components";

import { useAuth } from "../auth/AuthContext";
import { isAuthConfigured } from "../config";

import { AuthorizationError } from "./AuthorizationError";

interface RouteGuardProps {
  children: ReactNode;
  /**
   * Which Cognito group the caller must be in. When omitted, any
   * signed-in user passes. `Admin` implicitly satisfies `Consumer`
   * routes because admins can see everything consumers can.
   */
  requiredRole?: "Consumer" | "Admin";
}

export function RouteGuard({ children, requiredRole }: RouteGuardProps) {
  const { session, login } = useAuth();

  if (!session) {
    if (!isAuthConfigured()) {
      return (
        <div role="status">
          <Alert type="info" header="Sign-in not configured.">
            Set <code>VITE_HOSTED_UI_DOMAIN</code> and{" "}
            <code>VITE_USER_POOL_CLIENT_ID</code> in the build to enable
            Cognito authentication. This page is normally gated behind a
            login.
          </Alert>
        </div>
      );
    }
    return (
      <Box padding="m">
        <SpaceBetween size="s">
          <Box>You need to sign in to view this page.</Box>
          <Button variant="primary" onClick={() => login()}>
            Sign in
          </Button>
        </SpaceBetween>
      </Box>
    );
  }

  if (requiredRole === "Admin" && !session.isAdmin) {
    return <AuthorizationError />;
  }
  // Admin can access Consumer routes.
  if (requiredRole === "Consumer" && !session.isConsumer && !session.isAdmin) {
    return <AuthorizationError />;
  }

  return <>{children}</>;
}
